import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrCreateThread, sendChatMessage } from '../lib/api';
import { ChatThread, ChatMessage } from '../lib/types';
import { ChatHeader } from '../components/chat/ChatHeader';
import { MessageList } from '../components/chat/MessageList';
import { MessageInput } from '../components/chat/MessageInput';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
export function ChatThreadPage() {
  const {
    taskId
  } = useParams<{
    taskId: string;
  }>();
  const navigate = useNavigate();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Load thread on mount
  useEffect(() => {
    if (!taskId) return;
    const loadThread = async () => {
      try {
        setIsLoading(true);
        const data = await getOrCreateThread(taskId);
        setThread(data);
        setMessages(data.messages);
      } catch (err) {
        console.error('Failed to load thread:', err);
        setError('Failed to load conversation. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    loadThread();
  }, [taskId]);
  const handleSend = async (text: string) => {
    if (!thread || isSending) return;
    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    const tempMessage: ChatMessage = {
      id: tempId,
      role: 'user',
      content: text,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    setIsSending(true);
    try {
      const response = await sendChatMessage(thread.thread_id, text);
      // Replace temp message with real ones
      setMessages(prev => [...prev.filter(m => m.id !== tempId), response.user_message, response.assistant_message]);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove temp message and show error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };
  if (isLoading) {
    return <div className="h-screen flex flex-col items-center justify-center bg-background" data-id="element-2046">
        <Loader2 size={32} className="animate-spin text-primary mb-4" data-id="element-2047" />
        <p className="text-muted-foreground" data-id="element-2048">Loading conversation...</p>
      </div>;
  }
  if (error || !thread) {
    return <div className="h-screen flex flex-col items-center justify-center bg-background p-4 text-center" data-id="element-2049">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4" data-id="element-2050">
          <AlertCircle size={32} className="text-destructive" data-id="element-2051" />
        </div>
        <h2 className="text-xl font-semibold mb-2" data-id="element-2052">Something went wrong</h2>
        <p className="text-muted-foreground mb-6 max-w-md" data-id="element-2053">
          {error || 'Thread not found'}
        </p>
        <div className="flex gap-3" data-id="element-2054">
          <Button onClick={() => window.location.reload()} data-id="element-2055">Try Again</Button>
          <Button variant="outline" onClick={() => navigate('/')} data-id="element-2056">
            Go Home
          </Button>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-background" data-id="element-2057">
      <ChatHeader title={thread.transcript_title} quote={thread.transcript_quote} taskId={thread.task_id} data-id="element-2058" />

      <div className="flex-1 overflow-hidden flex flex-col max-w-3xl mx-auto w-full border-x border-border/40" data-id="element-2059">
        <MessageList messages={messages} isLoading={isSending} data-id="element-2060" />
        <MessageInput onSend={handleSend} disabled={isSending} data-id="element-2061" />
      </div>
    </div>;
}